import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import './BrandOnboarding.scss';

import { getUrlParam, setUrlParams } from '../../Helpers/helpers';
import { getBrand, getCompetitorBrands, getBrandSocialAccount, isSubscribedBrand } from '../../Helpers/user_helpers';
import { preloadImages } from '../../Helpers/image_helpers';
import {
  brandOnboardingPanelIsViewable,
  brandOnboardingPanelHasBeenViewed,
  brandOnboardingDomainSetupIsComplete,
  brandOnboardingGetDomainToSetup
} from '../../Helpers/onboarding_helpers';
import { syncCurrentUser } from '../../Actions/UserActions';

import BrandOnboardingHeader from '../../Components/BrandOnboarding/BrandOnboardingHeader';
import BrandOnboardingData from '../../Components/BrandOnboarding/BrandOnboardingData';
import BrandOnboardingPanelTransition from '../../Components/BrandOnboarding/BrandOnboardingPanelTransition';
import BrandOnboardingRegistrationPanel from '../../Components/BrandOnboarding/Panels/BrandOnboardingRegistrationPanel';
import BrandOnboardingCredentialsPanel from '../../Components/BrandOnboarding/Panels/BrandOnboardingCredentialsPanel';
import BrandOnboardingNamePanel from '../../Components/BrandOnboarding/Panels/BrandOnboardingNamePanel';
import BrandOnboardingDescriptionPanel from '../../Components/BrandOnboarding/Panels/BrandOnboardingDescriptionPanel';
import BrandOnboardingCompetitorsPanel from '../../Components/BrandOnboarding/Panels/BrandOnboardingCompetitorsPanel';
import BrandOnboardingImagePanel from '../../Components/BrandOnboarding/Panels/BrandOnboardingImagePanel';
import BrandOnboardingConnectSocialsPanel from '../../Components/BrandOnboarding/Panels/BrandOnboardingConnectSocialsPanel';
import BrandOnboardingNetworkPanel from '../../Components/BrandOnboarding/Panels/BrandOnboardingNetworkPanel';
import BrandOnboardingPOSPanel from '../../Components/BrandOnboarding/Panels/BrandOnboardingPOSPanel';
import BrandOnboardingCatalogPanel from '../../Components/BrandOnboarding/Panels/BrandOnboardingCatalogPanel';
import BrandOnboardingReviewPanel from '../../Components/BrandOnboarding/Panels/BrandOnboardingReviewPanel';
import BrandOnboardingInvoicePanel from '../../Components/BrandOnboarding/Panels/BrandOnboardingInvoicePanel';
import BrandOnboardingRecompletedPanel from '../../Components/BrandOnboarding/Panels/BrandOnboardingRecompletedPanel';

// Shopify / ShopMy Network Panels
import BrandOnboardingShopifyAppExplanation from '../../Components/BrandOnboarding/Panels/BrandOnboardingShopifyAppExplanation';
import BrandOnboardingDomainSetupPanel from '../../Components/BrandOnboarding/Panels/BrandOnboardingDomainSetupPanel';
import BrandOnboardingShopifyConnectionPanel from '../../Components/BrandOnboarding/Panels/BrandOnboardingShopifyConnectionPanel';

const BrandOnboarding = props => {
  const { onboarding, user, featured } = props;

  const sections = [
    {
      name: 'Registration',
      showInProgressBar: false,
      canView: () => true,
      panels: [
        {
          name: 'Registration Code',
          isHidden: () => false,
          canView: () => !onboarding.brand?.id && !getBrand(user)?.id,
          Component: BrandOnboardingRegistrationPanel,
          isComplete: !!onboarding.brand?.id || !!getBrand(user)?.id,
          isInProgress: false,
          backgroundImage: null,
          featuredStat: null
        },
        {
          name: 'Credentials',
          isHidden: () => false,
          canView: () => true,
          Component: BrandOnboardingCredentialsPanel,
          isComplete: !!getBrand(user)?.id,
          isInProgress: false,
          backgroundImage: 'https://askemma-static-public.s3.us-east-2.amazonaws.com/Onboarding/Brands/ob5.jpg',
          featuredStat: {
            number: featured.publicStats?.pinVolume,
            description: 'Total Revenue Generated for Brands'
          }
        }
      ]
    },
    {
      name: 'Brand Details',
      showInProgressBar: true,
      canView: () => true,
      panels: [
        {
          name: 'Brand Name',
          isHidden: () => false,
          canView: () => true,
          Component: BrandOnboardingNamePanel,
          isComplete: !!getBrand(user)?.name && !!getBrand(user)?.domain && brandOnboardingPanelHasBeenViewed(onboarding, 'Brand Name'),
          isInProgress: false,
          backgroundImage: 'https://askemma-static-public.s3.us-east-2.amazonaws.com/Onboarding/Brands/ob1.jpg',
          featuredStat: {
            number: featured.publicStats?.brandCount,
            description: 'Brands Partnered Directly with ShopMy'
          }
        },
        {
          name: 'Brand Description',
          isHidden: () => false,
          canView: () => true,
          Component: BrandOnboardingDescriptionPanel,
          isComplete: !!getBrand(user)?.description && brandOnboardingPanelHasBeenViewed(onboarding, 'Brand Description'),
          isInProgress: false,
          backgroundImage: 'https://askemma-static-public.s3.us-east-2.amazonaws.com/Onboarding/Brands/ob6.jpg',
          featuredStat: {
            number: featured.publicStats?.lookbookOrders,
            description: 'Automated Gifting Orders Processed by ShopMy'
          }
        },
        {
          name: 'Brand Competitors',
          isHidden: () => false,
          canView: () => true,
          Component: BrandOnboardingCompetitorsPanel,
          isComplete: getCompetitorBrands(user)?.length && brandOnboardingPanelHasBeenViewed(onboarding, 'Brand Competitors'),
          isInProgress: false,
          backgroundImage: 'https://askemma-static-public.s3.us-east-2.amazonaws.com/Onboarding/Brands/ob7.jpg',
          featuredStat: {
            number: featured.publicStats?.lookbookBrands,
            description: 'Brands Gifting to Creators'
          }
        },
        {
          name: 'Brand Images',
          isHidden: () => false,
          canView: () => true,
          Component: BrandOnboardingImagePanel,
          isComplete:
            !!getBrand(user)?.logo &&
            !!getBrand(user)?.mobileCoverImage &&
            !!getBrand(user)?.mobileBadgeImage &&
            brandOnboardingPanelHasBeenViewed(onboarding, 'Brand Images'),
          isInProgress: !!getBrand(user)?.logo || !!getBrand(user)?.mobileCoverImage || !!getBrand(user)?.mobileBadgeImage,
          backgroundImage: 'https://askemma-static-public.s3.us-east-2.amazonaws.com/Onboarding/Brands/review_panel_background.jpg',
          featuredStat: {
            number: featured.publicStats?.pinClicks,
            description: 'Total Clicks to Brand Websites Through ShopMy'
          }
        },
        {
          name: 'Connect Socials',
          isHidden: () => false,
          canView: () => true,
          Component: BrandOnboardingConnectSocialsPanel,
          isComplete: !!getBrandSocialAccount(user, 'instagram')?.id || onboarding.skipConnectingSocials,
          isInProgress: false,
          backgroundImage: 'https://askemma-static-public.s3.us-east-2.amazonaws.com/Onboarding/Brands/ob2.jpg',
          featuredStat: {
            number: featured.publicStats?.totalPins,
            description: 'Commissionable Links Created on ShopMy'
          }
        }
      ]
    },
    {
      name: 'Network Configuration',
      showInProgressBar: true,
      canView: () => true,
      panels: [
        {
          name: 'Affiliate Network',
          isHidden: () => false,
          canView: () => true,
          Component: BrandOnboardingNetworkPanel,
          isComplete: !!onboarding.integratingMerchant,
          isInProgress: false,
          backgroundImage: 'https://askemma-static-public.s3.us-east-2.amazonaws.com/Onboarding/Brands/ob3.png',
          featuredStat: {
            number: featured.publicStats?.catalogProducts,
            description: 'Brand Products in ShopMy Catalog'
          }
        },
        {
          name: 'POS System',
          isHidden: () => false,
          canView: () => true,
          Component: BrandOnboardingPOSPanel,
          isComplete: onboarding.usesShopify !== undefined,
          isInProgress: false,
          backgroundImage: 'https://askemma-static-public.s3.us-east-2.amazonaws.com/Onboarding/Brands/email_panel_background.png',
          featuredStat: {
            number: featured.publicStats?.lookbookCreators,
            description: 'Creators Gifted to by Brands'
          }
        },
        {
          name: 'Domain Setup',
          isHidden: () => false,
          canView: () => true,
          Component: BrandOnboardingDomainSetupPanel,
          isComplete: !brandOnboardingGetDomainToSetup(onboarding),
          isInProgress: onboarding.domainsToSetup?.some(d => d.setupInProgress),
          inProgressText: onboarding.domainsToSetup
            ? `${onboarding.domainsToSetup
                ?.map(d =>
                  brandOnboardingDomainSetupIsComplete(onboarding, d)
                    ? `${d.domain} (complete)`
                    : d.setupInProgress
                    ? `${d.domain} (setup incomplete)`
                    : d.domain
                )
                ?.join('\n')}`
            : '',
          backgroundImage: 'https://askemma-static-public.s3.us-east-2.amazonaws.com/Onboarding/Brands/url_panel_background.png',
          featuredStat: {
            number: featured.publicStats?.totalPaidOpportunities,
            description: 'Completed Sales Campaigns on ShopMy'
          }
        },
        {
          name: 'Shopify App Explanation',
          isHidden: () => !onboarding.usesShopify || brandOnboardingPanelHasBeenViewed(onboarding, 'Shopify App Connection'),
          canView: () => true,
          Component: BrandOnboardingShopifyAppExplanation,
          isComplete: !!brandOnboardingPanelHasBeenViewed(onboarding, 'Shopify App Explanation'),
          isInProgress: false,
          inProgressText: '',
          backgroundImage: 'https://askemma-static-public.s3.us-east-2.amazonaws.com/Onboarding/Brands/pitch_panel_background.jpg',
          featuredStat: null
        },
        {
          name: 'Shopify App Connection',
          isHidden: () => !onboarding.usesShopify,
          canView: () => true,
          Component: BrandOnboardingShopifyConnectionPanel,
          isComplete: !brandOnboardingGetDomainToSetup(onboarding),
          isInProgress: false,
          backgroundImage: 'https://askemma-static-public.s3.us-east-2.amazonaws.com/Onboarding/Brands/pitch_panel_background.jpg',
          featuredStat: null
        },
        {
          name: 'Catalog Integration',
          isHidden: () => true,
          canView: () => true,
          Component: BrandOnboardingCatalogPanel,
          isComplete: brandOnboardingPanelHasBeenViewed(onboarding, 'Catalog Integration'),
          isInProgress: false,
          backgroundImage: 'https://askemma-static-public.s3.us-east-2.amazonaws.com/Onboarding/Brands/email_panel_background.png',
          featuredState: null
        }
      ]
    },
    {
      name: 'Review',
      showInProgressBar: true,
      canView: () => true,
      panels: [
        {
          name: 'Account Review',
          isHidden: () => true,
          canView: () => true,
          Component: BrandOnboardingReviewPanel,
          isComplete: false,
          isInProgress: false,
          backgroundImage: 'https://askemma-static-public.s3.us-east-2.amazonaws.com/Onboarding/Brands/email_panel_background.png',
          featuredStat: null
        },
        {
          name: 'Invoice',
          isHidden: () => isSubscribedBrand(user),
          canView: () => true,
          Component: BrandOnboardingInvoicePanel,
          isComplete: false,
          isInProgress: false,
          backgroundImage: 'https://askemma-static-public.s3.us-east-2.amazonaws.com/Onboarding/Brands/email_panel_background.png',
          featuredStat: {
            number: featured.publicStats?.creatorCount,
            description: 'Creators Using ShopMy to Monetize Their Influence'
          }
        },
        {
          name: 'Updated Account',
          isHidden: () => !isSubscribedBrand(user),
          canView: () => true,
          Component: BrandOnboardingRecompletedPanel,
          isComplete: false,
          isInProgress: false,
          backgroundImage: 'https://askemma-static-public.s3.us-east-2.amazonaws.com/Onboarding/Brands/email_panel_background.png',
          featuredStat: {
            number: featured.publicStats?.creatorCount,
            description: 'Creators Using ShopMy to Monetize Their Influence'
          }
        }
      ]
    }
  ];

  const getStartingPanel = () => {
    // get the first incomplete panel that can be viewed
    for (const section of sections) {
      if (!section.canView()) continue;
      for (const panel of section.panels) {
        if (!brandOnboardingPanelIsViewable(panel)) continue;
        if (!panel.isComplete) return { sectionName: section.name, panelName: panel.name };
      }
    }
  };
  const [activeSectionName, setActiveSectionName] = useState(getUrlParam('section') || getStartingPanel()?.sectionName || 'Registration');
  const [activePanelName, setActivePanelName] = useState(getUrlParam('panel') || getStartingPanel()?.panelName || 'Registration Code');
  const activePanel = sections.find(section => section.name === activeSectionName)?.panels.find(panel => panel.name === activePanelName);
  const [isLoading, setIsLoading] = useState(false);
  const [prevPanel, setPrevPanel] = useState(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const getActivePanelNumber = () => {
    let panelNumber = 0;
    for (const section of sections) {
      for (const panel of section.panels.filter(brandOnboardingPanelIsViewable)) {
        if (panel.name === activePanelName && section.name === activeSectionName) {
          return panelNumber + 1; // +1 to make it 1-indexed
        }

        panelNumber++;
      }
    }
  };

  const getNextTarget = () => {
    const currentSectionIndex = sections.findIndex(sec => sec.name === activeSectionName);
    const currentSection = currentSectionIndex !== -1 ? sections[currentSectionIndex] : null;
    if (!currentSection) {
      window.ALERT?.error('Current section not found.');
      return null;
    }

    const viewable = currentSection.panels.filter(brandOnboardingPanelIsViewable);
    const currentIndexInViewable = viewable.findIndex(p => p.name === activePanelName);
    if (currentIndexInViewable === -1) {
      window.ALERT?.error('Current panel not found.');
      return null;
    }

    // Next panel in the same section
    if (currentIndexInViewable + 1 < viewable.length) {
      return { sectionName: currentSection.name, panelName: viewable[currentIndexInViewable + 1].name };
    }

    // Otherwise, advance to first viewable panel of the next section with at least one viewable panel
    for (let i = currentSectionIndex + 1; i < sections.length; i++) {
      const nextViewable = sections[i].panels.filter(brandOnboardingPanelIsViewable);
      if (nextViewable.length) {
        return { sectionName: sections[i].name, panelName: nextViewable[0].name };
      }
    }

    window.ALERT?.error('There is not another section to go to.');
    return null;
  };

  const getPreviousTarget = () => {
    const currentSectionIndex = sections.findIndex(sec => sec.name === activeSectionName);
    const currentSection = currentSectionIndex !== -1 ? sections[currentSectionIndex] : null;
    if (!currentSection) {
      window.ALERT?.error('Current section not found.');
      return null;
    }

    const viewable = currentSection.panels.filter(brandOnboardingPanelIsViewable);
    const currentIndexInViewable = viewable.findIndex(p => p.name === activePanelName);
    if (currentIndexInViewable === -1) {
      window.ALERT?.error('Current panel not found.');
      return null;
    }

    // Previous panel in the same section
    if (currentIndexInViewable - 1 >= 0) {
      return { sectionName: currentSection.name, panelName: viewable[currentIndexInViewable - 1].name };
    }

    // Otherwise, go to the last viewable panel of the previous section with at least one viewable panel
    for (let i = currentSectionIndex - 1; i >= 0; i--) {
      const prevViewable = sections[i].panels.filter(brandOnboardingPanelIsViewable);
      if (prevViewable.length) {
        return { sectionName: sections[i].name, panelName: prevViewable[prevViewable.length - 1].name };
      }
    }

    window.ALERT?.error('There is not a previous section to go to.');
    return null;
  };

  const goToPanel = async (sectionName, panelName, options) => {
    const { updateHistory = true } = options || {};
    if (isAnimating) return;

    const section = sections.find(sec => sec.name === sectionName);
    if (!section) return window.ALERT?.error(`Section "${sectionName}" not found.`);

    const panel = section.panels.find(p => p.name === panelName);
    if (!panel) return window.ALERT?.error(`Panel "${panelName}" not found in section "${sectionName}".`);
    if (!brandOnboardingPanelIsViewable(panel)) return window.ALERT.error(`You cannot access this panel at this time.`);

    if (!isLoading) setIsLoading(true);
    if (panel.backgroundImage) await preloadImages(panel.backgroundImage);

    // trigger transition
    setPrevPanel(activePanel);
    setIsAnimating(true);

    // update state + url
    setActiveSectionName(sectionName);
    setActivePanelName(panelName);
    if (updateHistory) setUrlParams({ section: sectionName, panel: panelName }, { shouldPushState: true, clearExisting: true });
    else setUrlParams({ section: sectionName, panel: panelName }, { shouldPushState: false, clearExisting: true });
    setIsLoading(false);
  };

  const goToNextPanel = () => {
    if (isAnimating) return;
    const target = getNextTarget();
    if (target) goToPanel(target.sectionName, target.panelName);
  };

  const goToPreviousPanel = () => {
    if (isAnimating) return;
    const target = getPreviousTarget();
    if (target) goToPanel(target.sectionName, target.panelName);
  };

  const getPanelProps = panel => ({
    goToNextPanel,
    goToPreviousPanel,
    goToPanel,
    activeSectionName,
    activePanelName,
    sections,
    panelNumber: getActivePanelNumber(),
    backgroundImage: panel?.backgroundImage,
    isLoading: isLoading || user?.isSyncing,
    setIsLoading,
    featuredStat: panel?.featuredStat
  });

  // sync with browser history (back/forward buttons)
  useEffect(() => {
    // Ensure the current entry has our state so Back/Forward works cleanly
    if (!window.history.state?.sectionName || !window.history.state?.panelName) {
      setUrlParams({ section: activeSectionName, panel: activePanelName }, { clearExisting: true });
    }

    const onPop = e => {
      const s = e.state;
      if (s?.sectionName && s?.panelName) {
        goToPanel(s.sectionName, s.panelName, { updateHistory: false });
        return;
      }

      // Fallback: read from URL if state is missing (e.g., user navigated directly)
      const params = new URL(window.location.href).searchParams;
      const section = params.get('section');
      const panel = params.get('panel');
      if (section && panel) {
        goToPanel(section, panel, { updateHistory: false });
      }
    };

    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  return (
    <div className='brand-onboarding-outer'>
      <BrandOnboardingData activePanelName={activePanelName} />
      <div className='brand-onboarding-inner'>
        <BrandOnboardingHeader />

        <React.Fragment>
          <BrandOnboardingPanelTransition Panel={activePanel?.Component} panelProps={getPanelProps(activePanel)} isActive />

          {/* outgoing panel – only present while fade completes */}
          {prevPanel && (
            <BrandOnboardingPanelTransition
              Panel={prevPanel?.Component}
              panelProps={getPanelProps(prevPanel)}
              onFadeOutEnd={() => {
                setPrevPanel(null);
                setIsAnimating(false);
              }}
              isActive={false}
            />
          )}
        </React.Fragment>
      </div>
    </div>
  );
};

BrandOnboarding.propTypes = {
  onboarding: PropTypes.object.isRequired,
  ui: PropTypes.object.isRequired
};

const mapStateToProps = state => ({
  onboarding: state.onboarding,
  user: state.user,
  ui: state.ui,
  featured: state.featured
});

export default connect(mapStateToProps, {
  syncCurrentUser
})(BrandOnboarding);
